import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Pagination, Row, Spinner, Table } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'

// ─── Constants ────────────────────────────────────────────────────────────────

const REQ_STATUS = [
  ['pending',   'รอดำเนินการ', 'warning'],
  ['approved',  'อนุมัติแล้ว', 'info'],
  ['issued',    'จ่ายแล้ว',    'success'],
  ['cancelled', 'ยกเลิก',      'secondary'],
]

const BORROW_STATUS = [
  ['borrowed', 'กำลังยืม',  'warning'],
  ['returned', 'คืนแล้ว',   'success'],
  ['overdue',  'เกินกำหนด', 'danger'],
]

const reqStatusMap    = Object.fromEntries(REQ_STATUS.map(([k, l, c]) => [k, { label: l, color: c }]))
const borrowStatusMap = Object.fromEntries(BORROW_STATUS.map(([k, l, c]) => [k, { label: l, color: c }]))

const PAGE_SIZES = [10, 20, 50, 100]
const DEFAULT_PAGE_SIZE = 20

const fmtMoney = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtNum   = (n) => Number(n || 0).toLocaleString('th-TH')
const fmtDate  = (d) => d ? String(d).slice(0, 10) : '-'
const toDateInput = (d) => { const v = fmtDate(d); return v === '-' ? '' : v }
const today = () => new Date().toISOString().slice(0, 10)

const getWeekRange = () => {
  const d = new Date()
  const day = d.getDay()
  const mon = new Date(d); mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1))
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6)
  return [mon.toISOString().slice(0, 10), sun.toISOString().slice(0, 10)]
}

const getMonthRange = () => {
  const d = new Date()
  const first = new Date(d.getFullYear(), d.getMonth(), 1)
  const last  = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  return [first.toISOString().slice(0, 10), last.toISOString().slice(0, 10)]
}

// ─── Pagination helpers ───────────────────────────────────────────────────────

function usePagination(data, defaultSize = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(defaultSize)

  // reset to page 1 whenever the data array reference changes (new load)
  useEffect(() => { setPage(1) }, [data])

  const total      = data.length
  const totalPages = Math.max(1, Math.ceil(total / size))
  const safePage   = Math.min(page, totalPages)
  const start      = (safePage - 1) * size
  const slice      = data.slice(start, start + size)

  const changeSize = (s) => { setSize(s); setPage(1) }

  return { page: safePage, setPage, size, changeSize, totalPages, slice, total, start }
}

function PaginationBar({ page, setPage, totalPages, total, start, size, changeSize }) {
  if (total === 0) return null
  const end = Math.min(start + size, total)

  const pageNums = []
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 2) pageNums.push(i)
  }
  const items = []
  let prev = 0
  for (const p of pageNums) {
    if (prev && p - prev > 1) items.push(<Pagination.Ellipsis key={`e${p}`} disabled />)
    items.push(
      <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
    )
    prev = p
  }

  return (
    <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top flex-wrap gap-2">
      <div className="d-flex align-items-center gap-2">
        <span className="text-muted fs-sm">แสดง {start + 1}–{end} จาก {total} รายการ</span>
        <Form.Select size="sm" style={{ width: 80 }} value={size} onChange={e => changeSize(Number(e.target.value))}>
          {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </Form.Select>
        <span className="text-muted fs-sm">ต่อหน้า</span>
      </div>
      <Pagination size="sm" className="mb-0">
        <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => p - 1)} />
        {items}
        <Pagination.Next disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
      </Pagination>
    </div>
  )
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

function StatusBadge({ value, map }) {
  const item = map[value] || { label: value || '-', color: 'secondary' }
  return <Badge bg={item.color}>{item.label}</Badge>
}

function StockBadge({ current, min }) {
  const n = Number(current || 0)
  const m = Number(min || 0)
  if (n === 0) return <Badge bg="danger">หมด</Badge>
  if (m > 0 && n <= m) return <Badge bg="warning" text="dark">ใกล้หมด</Badge>
  return <Badge bg="success">ปกติ</Badge>
}

function PageShell({ title, icon, subtitle, children, action }) {
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h4 className="main-title mb-1"><i className={`${icon} me-2 text-primary`} />{title}</h4>
              {subtitle && <div className="text-muted fs-sm">{subtitle}</div>}
            </div>
            {action}
          </div>
          {children}
        </Container>
        <Footer />
      </div>
    </>
  )
}

function StatCard({ icon, label, value, color = 'primary', hint }) {
  return (
    <Col sm={6} xl={3}>
      <Card className="card-one shadow-sm h-100">
        <Card.Body className="d-flex align-items-center gap-3">
          <div className={`rounded-3 bg-${color} bg-opacity-10 text-${color} d-flex align-items-center justify-content-center`} style={{ width: 52, height: 52, fontSize: 26 }}>
            <i className={icon} />
          </div>
          <div>
            <div className="text-muted fs-sm">{label}</div>
            <h3 className="mb-0">{value}</h3>
            {hint && <div className="fs-xs text-muted mt-1">{hint}</div>}
          </div>
        </Card.Body>
      </Card>
    </Col>
  )
}

function DateRangeFilter({ dateFrom, setDateFrom, dateTo, setDateTo, children }) {
  const set = (from, to) => { setDateFrom(from); setDateTo(to) }
  const todayStr = today()
  const [wMon, wSun] = getWeekRange()
  const [mFirst, mLast] = getMonthRange()
  const hasRange = dateFrom || dateTo

  return (
    <Row className="g-2 align-items-end">
      {children}
      <Col xs="auto">
        <Form.Label className="mb-1 fw-semibold">ช่วงด่วน</Form.Label>
        <div className="d-flex gap-1">
          <Button size="sm" variant={dateFrom === todayStr && dateTo === todayStr ? 'primary' : 'outline-secondary'} onClick={() => set(todayStr, todayStr)}>วันนี้</Button>
          <Button size="sm" variant={dateFrom === wMon && dateTo === wSun ? 'primary' : 'outline-secondary'} onClick={() => set(wMon, wSun)}>สัปดาห์นี้</Button>
          <Button size="sm" variant={dateFrom === mFirst && dateTo === mLast ? 'primary' : 'outline-secondary'} onClick={() => set(mFirst, mLast)}>เดือนนี้</Button>
        </div>
      </Col>
      <Col xs="auto">
        <Form.Label className="mb-1 fw-semibold">ตั้งแต่</Form.Label>
        <Form.Control type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: 155 }} />
      </Col>
      <Col xs="auto">
        <Form.Label className="mb-1 fw-semibold">ถึง</Form.Label>
        <Form.Control type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: 155 }} />
      </Col>
      {hasRange && (
        <Col xs="auto">
          <Button variant="outline-danger" size="sm" style={{ marginTop: 24 }} onClick={() => { setDateFrom(''); setDateTo('') }} title="ล้างวันที่">
            <i className="ri-close-line" />
          </Button>
        </Col>
      )}
    </Row>
  )
}

function SimpleModal({ show, onHide, title, children, onSave, saving, disabled, error, size = 'lg' }) {
  return (
    <Modal show={show} onHide={onHide} size={size} centered>
      <Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        {children}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>ปิด</Button>
        {onSave && <Button onClick={onSave} disabled={saving || disabled}>{saving ? 'กำลังบันทึก...' : 'บันทึก'}</Button>}
      </Modal.Footer>
    </Modal>
  )
}

function useMasterData() {
  const [master, setMaster] = useState({ categories: [], items: [], vendors: [], teachers: [] })
  const [error, setError] = useState('')
  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/supply/master-data')
      setMaster(res.data.data || { categories: [], items: [], vendors: [], teachers: [] })
    } catch (err) { setError(err.response?.data?.message || 'โหลดข้อมูลอ้างอิงไม่สำเร็จ') }
  }, [])
  useEffect(() => { load() }, [load])
  return { master, loadMaster: load, error }
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function SupplyDashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try { const res = await axiosInstance.get('/api/supply/dashboard'); setData(res.data.data) }
    catch (err) { setError(err.response?.data?.message || 'โหลด dashboard ไม่สำเร็จ') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const stockChart = useMemo(() => ({
    series: [
      Number(data?.itemStats?.out_of_stock_count || 0),
      Number(data?.itemStats?.low_stock_count || 0) - Number(data?.itemStats?.out_of_stock_count || 0),
      Number(data?.itemStats?.active_items || 0) - Number(data?.itemStats?.low_stock_count || 0),
    ],
    options: {
      chart: { type: 'donut', toolbar: { show: false } },
      labels: ['หมดสต็อก', 'ใกล้หมด', 'ปกติ'],
      colors: ['#dc3545', '#ffc107', '#198754'],
      legend: { position: 'bottom' },
    },
  }), [data])

  return (
    <PageShell title="Dashboard วัสดุและของใช้" icon="ri-store-3-line" subtitle="ภาพรวมคลังวัสดุ การเบิกจ่าย และการยืม-คืน">
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? <div className="text-center py-5"><Spinner animation="border" /></div> : (
        <>
          <Row className="g-3 mb-4">
            <StatCard icon="ri-stack-line"            label="รายการวัสดุทั้งหมด" value={fmtNum(data?.itemStats?.total_items)}     color="primary" />
            <StatCard icon="ri-alert-line"             label="วัสดุใกล้หมด/หมด"  value={fmtNum(data?.itemStats?.low_stock_count)}  color="warning" hint="รายการ" />
            <StatCard icon="ri-file-list-3-line"       label="รอเบิกจ่าย"         value={fmtNum(data?.reqStats?.pending_count)}    color="info" />
            <StatCard icon="ri-arrow-left-right-line"  label="กำลังยืม"           value={fmtNum(data?.borrowStats?.active_borrows)} color="danger" />
          </Row>
          <Row className="g-3 mb-4">
            <Col lg={4}>
              <Card className="card-one shadow-sm h-100">
                <Card.Header><Card.Title as="h6">สถานะสต็อกวัสดุ</Card.Title></Card.Header>
                <Card.Body>
                  {stockChart.series.some(v => v > 0)
                    ? <ReactApexChart options={stockChart.options} series={stockChart.series} type="donut" height={260} />
                    : <div className="text-center text-muted py-5">ยังไม่มีรายการวัสดุ</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col lg={8}>
              <Card className="card-one shadow-sm h-100">
                <Card.Header><Card.Title as="h6">วัสดุที่ใกล้หมด / หมดสต็อก</Card.Title></Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead><tr><th>รหัส</th><th>รายการ</th><th>คงเหลือ</th><th>จุดสั่ง</th><th>สถานะ</th></tr></thead>
                    <tbody>
                      {(data?.lowStockItems || []).length === 0
                        ? <tr><td colSpan={5} className="text-center text-muted py-3">ทุกรายการมีสต็อกเพียงพอ</td></tr>
                        : (data?.lowStockItems || []).map(r => (
                          <tr key={r.item_id}>
                            <td className="fw-semibold">{r.item_code}</td>
                            <td>{r.item_name}<div className="text-muted fs-xs">{r.category_name || '-'}</div></td>
                            <td>{fmtNum(r.current_stock)} {r.unit}</td>
                            <td>{fmtNum(r.min_stock)} {r.unit}</td>
                            <td><StockBadge current={r.current_stock} min={r.min_stock} /></td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="g-3">
            <Col lg={6}>
              <Card className="card-one shadow-sm">
                <Card.Header><Card.Title as="h6">รอเบิกจ่าย</Card.Title></Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead><tr><th>เลขที่</th><th>วันที่</th><th>ผู้เบิก</th><th>รายการ</th></tr></thead>
                    <tbody>
                      {(data?.pendingReqs || []).length === 0
                        ? <tr><td colSpan={4} className="text-center text-muted py-3">ไม่มีรายการรอเบิก</td></tr>
                        : (data?.pendingReqs || []).map(r => (
                          <tr key={r.req_id}>
                            <td className="fw-semibold">{r.req_no}</td>
                            <td>{fmtDate(r.req_date)}</td>
                            <td>{r.requester_name || '-'}</td>
                            <td><Badge bg="secondary">{r.item_count} รายการ</Badge></td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="card-one shadow-sm">
                <Card.Header><Card.Title as="h6">รับเข้าคลังล่าสุด</Card.Title></Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead><tr><th>วันที่</th><th>รายการ</th><th>จำนวน</th><th>มูลค่า</th></tr></thead>
                    <tbody>
                      {(data?.recentReceives || []).length === 0
                        ? <tr><td colSpan={4} className="text-center text-muted py-3">ยังไม่มีรายการรับ</td></tr>
                        : (data?.recentReceives || []).map(r => (
                          <tr key={r.receive_id}>
                            <td>{fmtDate(r.receive_date)}</td>
                            <td>{r.item_name}</td>
                            <td>{fmtNum(r.qty)} {r.unit}</td>
                            <td>{fmtMoney(r.qty * r.unit_price)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </PageShell>
  )
}

// ─── Items (รายการวัสดุ) ──────────────────────────────────────────────────────

function ItemModal({ show, onHide, onSaved, row, categories }) {
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { setForm(row || { item_status: 'active', unit: 'ชิ้น', unit_price: 0, min_stock: 0 }); setError('') }, [row, show])
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))
  const save = async () => {
    setSaving(true); setError('')
    try { await axiosInstance.post('/api/supply/items', form); onSaved(); onHide() }
    catch (err) { setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ') }
    finally { setSaving(false) }
  }
  return (
    <SimpleModal show={show} onHide={onHide} title={form.item_id ? 'แก้ไขรายการวัสดุ' : 'เพิ่มรายการวัสดุ'} onSave={save} saving={saving} disabled={!form.item_code || !form.item_name} error={error}>
      <Row className="g-3">
        <Col md={3}><Form.Label>รหัส *</Form.Label><Form.Control value={form.item_code || ''} onChange={e => set('item_code', e.target.value)} /></Col>
        <Col md={9}><Form.Label>ชื่อรายการ *</Form.Label><Form.Control value={form.item_name || ''} onChange={e => set('item_name', e.target.value)} /></Col>
        <Col md={4}><Form.Label>หมวดหมู่</Form.Label>
          <Form.Select value={form.category_id || ''} onChange={e => set('category_id', e.target.value)}>
            <option value="">ไม่ระบุ</option>
            {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
          </Form.Select>
        </Col>
        <Col md={2}><Form.Label>หน่วย</Form.Label><Form.Control value={form.unit || ''} onChange={e => set('unit', e.target.value)} /></Col>
        <Col md={3}><Form.Label>ราคาต่อหน่วย</Form.Label><Form.Control type="number" value={form.unit_price || 0} onChange={e => set('unit_price', e.target.value)} /></Col>
        <Col md={3}><Form.Label>จุดสั่งซื้อ (ขั้นต่ำ)</Form.Label><Form.Control type="number" value={form.min_stock || 0} onChange={e => set('min_stock', e.target.value)} /></Col>
        <Col md={4}><Form.Label>สถานะ</Form.Label>
          <Form.Select value={form.item_status || 'active'} onChange={e => set('item_status', e.target.value)}>
            <option value="active">ใช้งาน</option><option value="inactive">ปิดใช้งาน</option>
          </Form.Select>
        </Col>
        <Col md={12}><Form.Label>หมายเหตุ</Form.Label><Form.Control as="textarea" rows={2} value={form.item_note || ''} onChange={e => set('item_note', e.target.value)} /></Col>
      </Row>
    </SimpleModal>
  )
}

export function SupplyItems() {
  const { master, error: masterError } = useMasterData()
  const [rows, setRows] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState({ show: false, row: null })
  const [error, setError] = useState('')

  useEffect(() => { const t = setTimeout(() => setSearch(searchInput), 400); return () => clearTimeout(t) }, [searchInput])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const q = new URLSearchParams()
      if (search) q.set('search', search)
      if (categoryFilter) q.set('category_id', categoryFilter)
      const res = await axiosInstance.get(`/api/supply/items?${q}`)
      setRows(res.data.data || [])
    } catch (err) { setError(err.response?.data?.message || 'โหลดรายการไม่สำเร็จ') }
    finally { setLoading(false) }
  }, [search, categoryFilter])

  useEffect(() => { load() }, [load])

  const pag = usePagination(rows)

  return (
    <PageShell title="รายการวัสดุและของใช้" icon="ri-stack-line" subtitle="จัดการรายการวัสดุ จุดสั่งซื้อ และดูยอดคงเหลือ" action={<Button onClick={() => setModal({ show: true, row: null })}><i className="ri-add-line me-1" />เพิ่มรายการ</Button>}>
      {(error || masterError) && <Alert variant="danger">{error || masterError}</Alert>}
      <Card className="card-one shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col md={7}><Form.Control placeholder="ค้นหารหัส / ชื่อรายการ" value={searchInput} onChange={e => setSearchInput(e.target.value)} /></Col>
            <Col md={5}>
              <Form.Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                <option value="">ทุกหมวดหมู่</option>
                {master.categories.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className="card-one shadow-sm">
        <Card.Body className="p-0">
          {loading ? <div className="text-center py-5"><Spinner /></div> : (
            <>
              <Table responsive hover className="mb-0">
                <thead><tr><th>รหัส</th><th>รายการ</th><th>หมวดหมู่</th><th>หน่วย</th><th>ราคา/หน่วย</th><th className="text-end">คงเหลือ</th><th className="text-end">จุดสั่ง</th><th>สต็อก</th><th></th></tr></thead>
                <tbody>
                  {rows.length === 0
                    ? <tr><td colSpan={9} className="text-center text-muted py-4">ไม่พบรายการ</td></tr>
                    : pag.slice.map(r => (
                      <tr key={r.item_id}>
                        <td className="fw-semibold">{r.item_code}</td>
                        <td>{r.item_name}</td>
                        <td>{r.category_name || '-'}</td>
                        <td>{r.unit}</td>
                        <td>{fmtMoney(r.unit_price)}</td>
                        <td className="text-end fw-semibold">{fmtNum(r.current_stock)}</td>
                        <td className="text-end">{fmtNum(r.min_stock)}</td>
                        <td><StockBadge current={r.current_stock} min={r.min_stock} /></td>
                        <td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => setModal({ show: true, row: r })}>แก้ไข</Button></td>
                      </tr>
                    ))}
                </tbody>
              </Table>
              <PaginationBar {...pag} />
            </>
          )}
        </Card.Body>
      </Card>
      <ItemModal show={modal.show} row={modal.row} categories={master.categories} onHide={() => setModal({ show: false, row: null })} onSaved={load} />
    </PageShell>
  )
}

// ─── Receive (รับเข้าคลัง) ────────────────────────────────────────────────────

export function SupplyReceive() {
  const { master } = useMasterData()
  const [rows, setRows] = useState([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [form, setForm] = useState({})
  const [show, setShow] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try { const res = await axiosInstance.get('/api/supply/receive'); setRows(res.data.data || []) }
    catch (err) { setError(err.response?.data?.message || 'โหลดรายการไม่สำเร็จ') }
  }, [])
  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => rows.filter(r => {
    const d = fmtDate(r.receive_date)
    if (dateFrom && d < dateFrom) return false
    if (dateTo && d > dateTo) return false
    return true
  }), [rows, dateFrom, dateTo])

  const pag = usePagination(filtered)

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))
  const save = async () => {
    setSaving(true); setError('')
    try { await axiosInstance.post('/api/supply/receive', form); setShow(false); load() }
    catch (err) { setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ') }
    finally { setSaving(false) }
  }

  return (
    <PageShell title="รับวัสดุเข้าคลัง" icon="ri-download-2-line" subtitle="บันทึกการรับวัสดุ อุปกรณ์ เข้าคลังพัสดุ" action={<Button onClick={() => { setForm({ receive_date: today(), qty: 1, unit_price: 0 }); setError(''); setShow(true) }}><i className="ri-add-line me-1" />บันทึกรับเข้า</Button>}>
      {error && !show && <Alert variant="danger">{error}</Alert>}
      <Card className="card-one shadow-sm mb-3">
        <Card.Body>
          <DateRangeFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
        </Card.Body>
      </Card>
      <Card className="card-one shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead><tr><th>วันที่รับ</th><th>รายการ</th><th className="text-end">จำนวน</th><th className="text-end">ราคา/หน่วย</th><th className="text-end">มูลค่า</th><th>ผู้ขาย</th></tr></thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan={6} className="text-center text-muted py-4">ยังไม่มีรายการ</td></tr>
                : pag.slice.map(r => (
                  <tr key={r.receive_id}>
                    <td>{fmtDate(r.receive_date)}</td>
                    <td>{r.item_name}</td>
                    <td className="text-end fw-semibold">{fmtNum(r.qty)} {r.unit}</td>
                    <td className="text-end">{fmtMoney(r.unit_price)}</td>
                    <td className="text-end">{fmtMoney(r.qty * r.unit_price)}</td>
                    <td>{r.vendor_name || '-'}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
          <PaginationBar {...pag} />
        </Card.Body>
      </Card>

      <SimpleModal show={show} onHide={() => setShow(false)} title="บันทึกรับวัสดุเข้าคลัง" onSave={save} saving={saving} disabled={!form.item_id || !form.qty || !form.receive_date} error={error}>
        <Row className="g-3">
          <Col md={12}><Form.Label>รายการวัสดุ *</Form.Label>
            <Form.Select value={form.item_id || ''} onChange={e => set('item_id', e.target.value)}>
              <option value="">เลือกรายการ</option>
              {master.items.map(i => <option key={i.item_id} value={i.item_id}>{i.item_code} - {i.item_name} (คงเหลือ: {fmtNum(i.current_stock)} {i.unit})</option>)}
            </Form.Select>
          </Col>
          <Col md={4}><Form.Label>จำนวน *</Form.Label><Form.Control type="number" min="0.01" step="0.01" value={form.qty || ''} onChange={e => set('qty', e.target.value)} /></Col>
          <Col md={4}><Form.Label>ราคา/หน่วย (บาท)</Form.Label><Form.Control type="number" min="0" step="0.01" value={form.unit_price || 0} onChange={e => set('unit_price', e.target.value)} /></Col>
          <Col md={4}><Form.Label>วันที่รับ *</Form.Label><Form.Control type="date" value={toDateInput(form.receive_date)} onChange={e => set('receive_date', e.target.value)} /></Col>
          <Col md={12}><Form.Label>ผู้ขาย</Form.Label>
            <Form.Select value={form.vendor_id || ''} onChange={e => set('vendor_id', e.target.value)}>
              <option value="">ไม่ระบุ</option>
              {master.vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
            </Form.Select>
          </Col>
          <Col md={12}><Form.Label>หมายเหตุ</Form.Label><Form.Control as="textarea" rows={2} value={form.receive_note || ''} onChange={e => set('receive_note', e.target.value)} /></Col>
        </Row>
      </SimpleModal>
    </PageShell>
  )
}

// ─── Requisitions (ใบเบิกวัสดุ) ──────────────────────────────────────────────

function ReqModal({ show, onHide, onSaved, row, master }) {
  const [form, setForm] = useState({})
  const [formItems, setFormItems] = useState([])
  const [addItem, setAddItem] = useState({ item_id: '', qty_requested: 1 })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!show) return
    setForm(row || { req_no: `REQ-${Date.now()}`, req_date: today(), req_status: 'pending' })
    setFormItems(row?.items?.map(it => ({ ...it, _key: it.req_item_id })) || [])
    setAddItem({ item_id: '', qty_requested: 1 })
    setError('')
  }, [row, show])

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const addRow = () => {
    if (!addItem.item_id || Number(addItem.qty_requested) <= 0) return
    const found = master.items.find(i => String(i.item_id) === String(addItem.item_id))
    if (!found) return
    if (formItems.some(it => String(it.item_id) === String(addItem.item_id))) {
      setError('รายการนี้มีอยู่แล้วในใบเบิก'); return
    }
    setFormItems(p => [...p, { _key: Date.now(), item_id: found.item_id, item_name: found.item_name, unit: found.unit, current_stock: found.current_stock, qty_requested: Number(addItem.qty_requested) }])
    setAddItem({ item_id: '', qty_requested: 1 })
    setError('')
  }

  const removeRow = (key) => setFormItems(p => p.filter(it => it._key !== key))

  const save = async () => {
    if (formItems.length === 0) { setError('กรุณาเพิ่มรายการวัสดุอย่างน้อย 1 รายการ'); return }
    setSaving(true); setError('')
    try { await axiosInstance.post('/api/supply/requisitions', { ...form, items: formItems }); onSaved(); onHide() }
    catch (err) { setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ') }
    finally { setSaving(false) }
  }

  const isIssued = form.req_status === 'issued' || form.req_status === 'cancelled'

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton><Modal.Title><i className="ri-file-list-3-line me-2" />{form.req_id ? 'แก้ไขใบเบิกวัสดุ' : 'สร้างใบเบิกวัสดุ'}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        <Row className="g-3 mb-3">
          <Col md={3}><Form.Label>เลขที่ใบเบิก *</Form.Label><Form.Control value={form.req_no || ''} onChange={e => set('req_no', e.target.value)} disabled={isIssued} /></Col>
          <Col md={3}><Form.Label>วันที่ *</Form.Label><Form.Control type="date" value={toDateInput(form.req_date)} onChange={e => set('req_date', e.target.value)} disabled={isIssued} /></Col>
          <Col md={3}><Form.Label>ผู้เบิก</Form.Label>
            <Form.Select value={form.requester_teacher_id || ''} onChange={e => set('requester_teacher_id', e.target.value)} disabled={isIssued}>
              <option value="">ไม่ระบุ</option>
              {master.teachers.map(t => <option key={t.t_id} value={t.t_id}>{t.t_name}</option>)}
            </Form.Select>
          </Col>
          <Col md={3}><Form.Label>สถานะ</Form.Label>
            <Form.Select value={form.req_status || 'pending'} onChange={e => set('req_status', e.target.value)} disabled={isIssued}>
              {REQ_STATUS.filter(([k]) => k !== 'issued').map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </Form.Select>
          </Col>
          <Col md={12}><Form.Label>วัตถุประสงค์</Form.Label><Form.Control value={form.purpose || ''} onChange={e => set('purpose', e.target.value)} disabled={isIssued} /></Col>
        </Row>
        <div className="border rounded p-3 mb-2">
          <div className="fw-semibold mb-2 text-primary">รายการที่ขอเบิก</div>
          {!isIssued && (
            <Row className="g-2 mb-3 align-items-end">
              <Col md={7}>
                <Form.Select value={addItem.item_id} onChange={e => setAddItem(p => ({ ...p, item_id: e.target.value }))}>
                  <option value="">เลือกรายการวัสดุ</option>
                  {master.items.map(i => <option key={i.item_id} value={i.item_id}>{i.item_code} - {i.item_name} (คงเหลือ: {fmtNum(i.current_stock)} {i.unit})</option>)}
                </Form.Select>
              </Col>
              <Col md={3}><Form.Control type="number" min="0.01" step="0.01" placeholder="จำนวน" value={addItem.qty_requested} onChange={e => setAddItem(p => ({ ...p, qty_requested: e.target.value }))} /></Col>
              <Col md={2}><Button className="w-100" variant="outline-primary" onClick={addRow}><i className="ri-add-line" /> เพิ่ม</Button></Col>
            </Row>
          )}
          <Table responsive hover className="mb-0" size="sm">
            <thead className="table-light"><tr><th>รายการ</th><th className="text-end">คงเหลือ</th><th className="text-end">จำนวนขอ</th><th className="text-end">จำนวนจ่าย</th>{!isIssued && <th></th>}</tr></thead>
            <tbody>
              {formItems.length === 0
                ? <tr><td colSpan={5} className="text-center text-muted py-2">ยังไม่มีรายการ</td></tr>
                : formItems.map(it => (
                  <tr key={it._key}>
                    <td>{it.item_name}<span className="text-muted ms-1 fs-xs">({it.unit})</span></td>
                    <td className="text-end">{fmtNum(it.current_stock)}</td>
                    <td className="text-end fw-semibold">{fmtNum(it.qty_requested)}</td>
                    <td className="text-end">{it.qty_issued != null ? fmtNum(it.qty_issued) : '-'}</td>
                    {!isIssued && <td className="text-end"><Button size="sm" variant="outline-danger" onClick={() => removeRow(it._key)}><i className="ri-delete-bin-line" /></Button></td>}
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>ปิด</Button>
        {!isIssued && <Button onClick={save} disabled={saving || !form.req_no || !form.req_date}>{saving ? 'กำลังบันทึก...' : 'บันทึก'}</Button>}
      </Modal.Footer>
    </Modal>
  )
}

function IssueModal({ show, onHide, onSaved, req }) {
  const [items, setItems] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!req) return
    setItems((req.items || []).map(it => ({ ...it, qty_issued: it.qty_issued > 0 ? it.qty_issued : it.qty_requested })))
    setError('')
  }, [req])

  const setQty = (req_item_id, val) =>
    setItems(p => p.map(it => it.req_item_id === req_item_id ? { ...it, qty_issued: Number(val) } : it))

  const save = async () => {
    setSaving(true); setError('')
    try { await axiosInstance.post(`/api/supply/requisitions/${req.req_id}/issue`, { items }); onSaved(); onHide() }
    catch (err) { setError(err.response?.data?.message || 'จ่ายวัสดุไม่สำเร็จ') }
    finally { setSaving(false) }
  }

  return (
    <SimpleModal show={show} onHide={onHide} title={`จ่ายวัสดุ — ${req?.req_no || ''}`} onSave={save} saving={saving} disabled={items.length === 0} error={error}>
      <p className="text-muted mb-3">ตรวจสอบและปรับจำนวนที่จ่ายจริงก่อนยืนยัน (ระบบจะหักสต็อก)</p>
      <Table responsive hover size="sm">
        <thead className="table-light"><tr><th>รายการ</th><th className="text-end">คงเหลือ</th><th className="text-end">ขอเบิก</th><th className="text-end" style={{ width: 130 }}>จ่ายจริง</th></tr></thead>
        <tbody>
          {items.map(it => (
            <tr key={it.req_item_id}>
              <td>{it.item_name}<span className="text-muted ms-1 fs-xs">({it.unit})</span></td>
              <td className="text-end">{fmtNum(it.current_stock)}</td>
              <td className="text-end">{fmtNum(it.qty_requested)}</td>
              <td>
                <Form.Control size="sm" type="number" min="0" max={it.qty_requested} step="0.01"
                  value={it.qty_issued} onChange={e => setQty(it.req_item_id, e.target.value)} className="text-end" />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </SimpleModal>
  )
}

export function SupplyRequisitions() {
  const { master } = useMasterData()
  const [rows, setRows] = useState([])
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [modal, setModal] = useState({ show: false, row: null })
  const [issueModal, setIssueModal] = useState({ show: false, req: null })
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try { const res = await axiosInstance.get('/api/supply/requisitions'); setRows(res.data.data || []) }
    catch (err) { setError(err.response?.data?.message || 'โหลดรายการไม่สำเร็จ') }
  }, [])
  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => rows.filter(r => {
    if (statusFilter && r.req_status !== statusFilter) return false
    const d = fmtDate(r.req_date)
    if (dateFrom && d < dateFrom) return false
    if (dateTo && d > dateTo) return false
    return true
  }), [rows, statusFilter, dateFrom, dateTo])

  const pag = usePagination(filtered)

  return (
    <PageShell title="ใบเบิกวัสดุ" icon="ri-file-list-3-line" subtitle="สร้างและจัดการใบเบิกวัสดุ อนุมัติ และจ่ายวัสดุออกจากคลัง" action={<Button onClick={() => setModal({ show: true, row: null })}><i className="ri-add-line me-1" />สร้างใบเบิก</Button>}>
      {error && <Alert variant="danger">{error}</Alert>}
      <Card className="card-one shadow-sm mb-3">
        <Card.Body>
          <DateRangeFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}>
            <Col xs="auto">
              <Form.Label className="mb-1 fw-semibold">สถานะ</Form.Label>
              <Form.Select style={{ width: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">ทุกสถานะ</option>
                {REQ_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </Form.Select>
            </Col>
          </DateRangeFilter>
        </Card.Body>
      </Card>
      <Card className="card-one shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead><tr><th>เลขที่</th><th>วันที่</th><th>ผู้เบิก</th><th>รายการ</th><th>สถานะ</th><th>วันที่จ่าย</th><th></th></tr></thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan={7} className="text-center text-muted py-4">ยังไม่มีใบเบิก</td></tr>
                : pag.slice.map(r => (
                  <tr key={r.req_id}>
                    <td className="fw-semibold">{r.req_no}</td>
                    <td>{fmtDate(r.req_date)}</td>
                    <td>{r.requester_name || '-'}</td>
                    <td><Badge bg="secondary">{(r.items || []).length} รายการ</Badge></td>
                    <td><StatusBadge value={r.req_status} map={reqStatusMap} /></td>
                    <td>{fmtDate(r.issued_date)}</td>
                    <td className="text-end d-flex gap-1 justify-content-end">
                      <Button size="sm" variant="outline-primary" onClick={() => setModal({ show: true, row: r })}>แก้ไข</Button>
                      {(r.req_status === 'pending' || r.req_status === 'approved') && (
                        <Button size="sm" variant="outline-success" onClick={() => setIssueModal({ show: true, req: r })}>
                          <i className="ri-send-plane-line me-1" />จ่าย
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
          <PaginationBar {...pag} />
        </Card.Body>
      </Card>
      <ReqModal show={modal.show} row={modal.row} master={master} onHide={() => setModal({ show: false, row: null })} onSaved={load} />
      <IssueModal show={issueModal.show} req={issueModal.req} onHide={() => setIssueModal({ show: false, req: null })} onSaved={load} />
    </PageShell>
  )
}

// ─── Borrow (ยืม-คืน) ────────────────────────────────────────────────────────

function ReturnModal({ show, onHide, onSaved, borrow }) {
  const [qty, setQty] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!borrow) return
    setQty(Number(borrow.qty_borrowed) - Number(borrow.qty_returned))
    setError('')
  }, [borrow])

  const save = async () => {
    setSaving(true); setError('')
    try { await axiosInstance.post(`/api/supply/borrow/${borrow.borrow_id}/return`, { qty_returned: qty }); onSaved(); onHide() }
    catch (err) { setError(err.response?.data?.message || 'บันทึกคืนไม่สำเร็จ') }
    finally { setSaving(false) }
  }

  const remaining = borrow ? Number(borrow.qty_borrowed) - Number(borrow.qty_returned) : 0

  return (
    <SimpleModal show={show} onHide={onHide} title="บันทึกคืนวัสดุ" onSave={save} saving={saving} disabled={!qty || Number(qty) <= 0} error={error} size="sm">
      {borrow && (
        <div className="mb-3 p-3 bg-light rounded">
          <div className="fw-semibold">{borrow.item_name}</div>
          <div className="text-muted fs-sm">ยืมไป {fmtNum(borrow.qty_borrowed)} {borrow.unit} | คืนแล้ว {fmtNum(borrow.qty_returned)} | คงค้าง {fmtNum(remaining)}</div>
        </div>
      )}
      <Form.Label>จำนวนที่คืน *</Form.Label>
      <Form.Control type="number" min="0.01" max={remaining} step="0.01" value={qty} onChange={e => setQty(e.target.value)} />
    </SimpleModal>
  )
}

export function SupplyBorrow() {
  const { master } = useMasterData()
  const [rows, setRows] = useState([])
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [form, setForm] = useState({})
  const [show, setShow] = useState(false)
  const [returnModal, setReturnModal] = useState({ show: false, borrow: null })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try { const res = await axiosInstance.get('/api/supply/borrow'); setRows(res.data.data || []) }
    catch (err) { setError(err.response?.data?.message || 'โหลดรายการไม่สำเร็จ') }
  }, [])
  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => rows.filter(r => {
    if (statusFilter && r.borrow_status !== statusFilter) return false
    const d = fmtDate(r.borrow_date)
    if (dateFrom && d < dateFrom) return false
    if (dateTo && d > dateTo) return false
    return true
  }), [rows, statusFilter, dateFrom, dateTo])

  const pag = usePagination(filtered)

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))
  const save = async () => {
    setSaving(true); setError('')
    try { await axiosInstance.post('/api/supply/borrow', form); setShow(false); load() }
    catch (err) { setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ') }
    finally { setSaving(false) }
  }

  return (
    <PageShell title="ยืม-คืนวัสดุ" icon="ri-arrow-left-right-line" subtitle="บันทึกการยืมและคืนวัสดุอุปกรณ์" action={<Button onClick={() => { setForm({ borrow_no: `BRW-${Date.now()}`, borrow_date: today(), qty_borrowed: 1 }); setError(''); setShow(true) }}><i className="ri-add-line me-1" />บันทึกการยืม</Button>}>
      {error && !show && <Alert variant="danger">{error}</Alert>}
      <Card className="card-one shadow-sm mb-3">
        <Card.Body>
          <DateRangeFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}>
            <Col xs="auto">
              <Form.Label className="mb-1 fw-semibold">สถานะ</Form.Label>
              <Form.Select style={{ width: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">ทุกสถานะ</option>
                {BORROW_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </Form.Select>
            </Col>
          </DateRangeFilter>
        </Card.Body>
      </Card>
      <Card className="card-one shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead><tr><th>เลขที่</th><th>รายการ</th><th>ผู้ยืม</th><th className="text-end">ยืม</th><th className="text-end">คืนแล้ว</th><th className="text-end">คงค้าง</th><th>วันยืม</th><th>กำหนดคืน</th><th>สถานะ</th><th></th></tr></thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan={10} className="text-center text-muted py-4">ยังไม่มีรายการ</td></tr>
                : pag.slice.map(r => (
                  <tr key={r.borrow_id}>
                    <td className="fw-semibold">{r.borrow_no}</td>
                    <td>{r.item_name}</td>
                    <td>{r.borrower_name || '-'}</td>
                    <td className="text-end">{fmtNum(r.qty_borrowed)} {r.unit}</td>
                    <td className="text-end">{fmtNum(r.qty_returned)}</td>
                    <td className="text-end fw-semibold">{fmtNum(Number(r.qty_borrowed) - Number(r.qty_returned))}</td>
                    <td>{fmtDate(r.borrow_date)}</td>
                    <td>{fmtDate(r.return_due_date)}</td>
                    <td><StatusBadge value={r.borrow_status} map={borrowStatusMap} /></td>
                    <td className="text-end">
                      {(r.borrow_status === 'borrowed' || r.borrow_status === 'overdue') && (
                        <Button size="sm" variant="outline-success" onClick={() => setReturnModal({ show: true, borrow: r })}>
                          <i className="ri-arrow-go-back-line me-1" />คืน
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
          <PaginationBar {...pag} />
        </Card.Body>
      </Card>

      <SimpleModal show={show} onHide={() => setShow(false)} title="บันทึกการยืมวัสดุ" onSave={save} saving={saving} disabled={!form.borrow_no || !form.item_id || !form.qty_borrowed || !form.borrow_date} error={error}>
        <Row className="g-3">
          <Col md={4}><Form.Label>เลขที่ *</Form.Label><Form.Control value={form.borrow_no || ''} onChange={e => set('borrow_no', e.target.value)} /></Col>
          <Col md={8}><Form.Label>รายการวัสดุ *</Form.Label>
            <Form.Select value={form.item_id || ''} onChange={e => set('item_id', e.target.value)}>
              <option value="">เลือกรายการ</option>
              {master.items.map(i => <option key={i.item_id} value={i.item_id}>{i.item_code} - {i.item_name} (คงเหลือ: {fmtNum(i.current_stock)} {i.unit})</option>)}
            </Form.Select>
          </Col>
          <Col md={4}><Form.Label>จำนวนที่ยืม *</Form.Label><Form.Control type="number" min="0.01" step="0.01" value={form.qty_borrowed || ''} onChange={e => set('qty_borrowed', e.target.value)} /></Col>
          <Col md={4}><Form.Label>วันที่ยืม *</Form.Label><Form.Control type="date" value={toDateInput(form.borrow_date)} onChange={e => set('borrow_date', e.target.value)} /></Col>
          <Col md={4}><Form.Label>กำหนดคืน</Form.Label><Form.Control type="date" value={toDateInput(form.return_due_date)} onChange={e => set('return_due_date', e.target.value)} /></Col>
          <Col md={12}><Form.Label>ผู้ยืม</Form.Label>
            <Form.Select value={form.borrower_teacher_id || ''} onChange={e => set('borrower_teacher_id', e.target.value)}>
              <option value="">ไม่ระบุ</option>
              {master.teachers.map(t => <option key={t.t_id} value={t.t_id}>{t.t_name}</option>)}
            </Form.Select>
          </Col>
          <Col md={12}><Form.Label>หมายเหตุ</Form.Label><Form.Control as="textarea" rows={2} value={form.borrow_note || ''} onChange={e => set('borrow_note', e.target.value)} /></Col>
        </Row>
      </SimpleModal>

      <ReturnModal show={returnModal.show} borrow={returnModal.borrow} onHide={() => setReturnModal({ show: false, borrow: null })} onSaved={load} />
    </PageShell>
  )
}

// ─── Settings (ตั้งค่า) ───────────────────────────────────────────────────────

export function SupplySettings() {
  const { master, loadMaster } = useMasterData()
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const save = async () => {
    setSaving(true); setError('')
    try { await axiosInstance.post('/api/supply/categories', form); setForm({}); loadMaster() }
    catch (err) { setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ') }
    finally { setSaving(false) }
  }

  return (
    <PageShell title="ตั้งค่าวัสดุและของใช้" icon="ri-settings-3-line" subtitle="จัดการหมวดหมู่วัสดุอุปกรณ์">
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      <Card className="card-one shadow-sm" style={{ maxWidth: 640 }}>
        <Card.Header><Card.Title as="h6">หมวดหมู่วัสดุ</Card.Title></Card.Header>
        <Card.Body>
          <Row className="g-2 mb-3">
            <Col md={3}><Form.Control placeholder="รหัส" value={form.category_code || ''} onChange={e => setForm(p => ({ ...p, category_code: e.target.value }))} /></Col>
            <Col md={6}><Form.Control placeholder="ชื่อหมวดหมู่" value={form.category_name || ''} onChange={e => setForm(p => ({ ...p, category_name: e.target.value }))} /></Col>
            <Col md={3}><Button className="w-100" onClick={save} disabled={saving || !form.category_code || !form.category_name}>{saving ? '...' : 'บันทึก'}</Button></Col>
          </Row>
          <Table responsive hover>
            <tbody>
              {master.categories.map(c => (
                <tr key={c.category_id}>
                  <td className="fw-semibold">{c.category_code}</td>
                  <td>{c.category_name}</td>
                  <td className="text-end"><Button size="sm" variant="outline-primary" onClick={() => setForm(c)}>แก้ไข</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </PageShell>
  )
}
